import type {
  CreateProductReviewInput,
  VerocAwaitingPaymentLotsQuery,
  VerocClaimFragment,
  VerocCoinsWalletFragment,
  VerocHistoryQuery,
  VerocLotsQuery,
  VerocNotificationsQuery,
  VerocOrderRewardFragment,
  VerocProgrammeFragment,
  VerocReviewPurchasesQuery,
  VerocRewardsQuery,
} from '#graphql-operations'
import type { Composer } from 'vue-i18n'
import { isMobileRewardMission, isRewardMissionVisible } from '~/utils/rewards'

export type RewardsWallet = VerocCoinsWalletFragment
export type RewardsProgramme = VerocProgrammeFragment
export type RewardsAccount = NonNullable<VerocRewardsQuery['activeCustomerVerocRewards']>
export type RewardsBenefits = VerocRewardsQuery['activeCustomerVerocCoinsBenefits']
export type RewardsClaim = VerocClaimFragment
export type RewardsMission = RewardsAccount['missions'][number]
export type RewardsMilestone = RewardsAccount['milestones'][number]
export type RewardsOrder = VerocOrderRewardFragment
export type RewardsHistoryEntry = VerocHistoryQuery['activeCustomerVerocCoinsHistory']['items'][number]
export type RewardsLot = VerocLotsQuery['activeCustomerVerocCoinsLots']['items'][number]
export type RewardsAwaitingPayment = VerocAwaitingPaymentLotsQuery['activeCustomerVerocCoinsLots']['items'][number]
export type RewardsNotification = VerocNotificationsQuery['activeCustomerVerocNotifications']['items'][number]
export type RewardsReviewPurchase = VerocReviewPurchasesQuery['activeCustomerVerocReviewPurchases']['items'][number]
export interface RewardsPage<T> {
  items: T[]
  totalItems: number
  loading: boolean
  loaded: boolean
  error: string | null
}
interface RewardsState {
  scope: string
  generation: number
  revision: number
  loaded: boolean
  loading: boolean
  programmeLoading: boolean
  busy: boolean
  error: string | null
  wallet: RewardsWallet | null
  programme: RewardsProgramme | null
  account: RewardsAccount | null
  benefits: RewardsBenefits | null
  claimResult: RewardsClaim | null
  history: RewardsPage<RewardsHistoryEntry>
  lots: RewardsPage<RewardsLot>
  awaitingPayment: RewardsPage<RewardsAwaitingPayment>
  notifications: RewardsPage<RewardsNotification>
  orderRewards: RewardsPage<RewardsOrder>
  reviewPurchases: RewardsPage<RewardsReviewPurchase>
}

const overviewRequests = new WeakMap<object, { generation: number, promise: Promise<void> }>()

const page = <T>(): RewardsPage<T> => ({ items: [], totalItems: 0, loading: false, loaded: false, error: null })
function initialState(scope: string, generation: number): RewardsState {
  return {
    scope,
    generation,
    revision: 0,
    loaded: false,
    loading: false,
    programmeLoading: false,
    busy: false,
    error: null,
    wallet: null,
    programme: null,
    account: null,
    benefits: null,
    claimResult: null,
    history: page(),
    lots: page(),
    awaitingPayment: page(),
    notifications: page(),
    orderRewards: page(),
    reviewPurchases: page(),
  }
}
const failureCode = (error: unknown) => error instanceof Error ? error.message : 'FAILED'
function throwOnErrors(errors: readonly { message?: string }[] | undefined) {
  if (errors?.length)
    throw new Error(errors[0]?.message || 'FAILED')
}

/**
 * One browser-only rewards snapshot shared by the drawer, header and checkout.
 * Scope changes synchronously discard private data, and an in-flight response can
 * only update the same customer/market generation. No reads happen during SSR.
 */
export function useRewards() {
  const nuxtApp = useNuxtApp()
  const auth = useAuth()
  const { locale } = nuxtApp.$i18n as Composer
  const customerId = computed(() => auth?.activeCustomer.value?.id == null ? null : String(auth.activeCustomer.value.id))
  const scope = computed(() => `${locale.value}:${customerId.value ?? 'guest'}`)
  const state = useState<RewardsState>('rewards', () => initialState(scope.value, 0))
  watch(scope, (value) => {
    if (state.value.scope !== value)
      state.value = initialState(value, state.value.generation + 1)
  }, { immediate: true, flush: 'sync' })
  const current = (generation: number) => state.value.generation === generation && state.value.scope === scope.value
  const field = <K extends keyof RewardsState>(key: K) => computed(() => state.value[key])
  // GraphQL composables use Nuxt context; restore it after awaits in click handlers.
  const inContext = <T>(fn: () => T) => nuxtApp.runWithContext(fn)

  function requireCurrentCustomer(customer: { id: string | number } | null | undefined) {
    if (!customer || String(customer.id) !== customerId.value)
      throw new Error('NOT_LOGGED_IN')
  }

  async function loadProgramme(force = false): Promise<void> {
    if (import.meta.server || state.value.programmeLoading || (state.value.programme && !force))
      return
    const generation = state.value.generation
    state.value.programmeLoading = true
    try {
      const { data, errors } = await inContext(() => useGraphqlQuery('verocProgramme'))
      if (!current(generation))
        return
      throwOnErrors(errors)
      if (!data?.verocCoinsProgramme)
        throw new Error('FAILED')
      state.value.programme = data.verocCoinsProgramme
    }
    catch (error) {
      if (current(generation))
        state.value.error = failureCode(error)
    }
    finally {
      if (current(generation))
        state.value.programmeLoading = false
    }
  }

  async function load(force = false): Promise<void> {
    if (import.meta.server)
      return
    if (auth && !auth.isAuthResolved.value) {
      try {
        await auth.refreshActiveCustomer()
      }
      catch (error) {
        state.value.error = failureCode(error)
        return
      }
    }
    const generation = state.value.generation
    const existing = overviewRequests.get(nuxtApp)
    if (existing?.generation === generation) {
      await existing.promise
      // A forced read (notably before account deletion) must actually finish a
      // new server read rather than silently reuse a pending older snapshot.
      if (force && current(generation))
        await load(true)
      return
    }
    if (state.value.loaded && !force)
      return
    const promise = readOverview(generation)
    overviewRequests.set(nuxtApp, { generation, promise })
    try {
      await promise
    }
    finally {
      if (overviewRequests.get(nuxtApp)?.promise === promise)
        overviewRequests.delete(nuxtApp)
    }
  }

  async function readOverview(generation: number): Promise<void> {
    state.value.loading = true
    state.value.error = null
    try {
      for (;;) {
        const revision = state.value.revision
        if (customerId.value) {
          const { data, errors } = await inContext(() => useGraphqlQuery('verocRewards'))
          if (!current(generation))
            return
          throwOnErrors(errors)
          requireCurrentCustomer(data?.activeCustomer)
          if (!data?.activeCustomerVerocRewards || !data.activeCustomerVerocCoins)
            throw new Error('NOT_LOGGED_IN')
          // A claim has committed since this read began. Do not restore its old balance.
          if (revision !== state.value.revision)
            continue
          Object.assign(state.value, {
            wallet: data.activeCustomerVerocCoins,
            programme: data.verocCoinsProgramme,
            account: data.activeCustomerVerocRewards,
            benefits: data.activeCustomerVerocCoinsBenefits,
          })
        }
        else {
          const { data, errors } = await inContext(() => useGraphqlQuery('verocProgramme'))
          if (!current(generation))
            return
          throwOnErrors(errors)
          if (!data?.verocCoinsProgramme)
            throw new Error('FAILED')
          state.value.programme = data.verocCoinsProgramme
        }
        state.value.loaded = true
        return
      }
    }
    catch (error) {
      if (current(generation)) {
        state.value.error = failureCode(error)
        if (failureCode(error) === 'NOT_LOGGED_IN') {
          state.value = initialState(scope.value, generation + 1)
          state.value.error = 'NOT_LOGGED_IN'
        }
      }
    }
    finally {
      if (current(generation))
        state.value.loading = false
    }
  }

  type PageKey = 'history' | 'lots' | 'awaitingPayment' | 'notifications' | 'orderRewards' | 'reviewPurchases'
  async function readPage<K extends PageKey>(key: K, read: (skip: number) => Promise<{ items: RewardsState[K]['items'], totalItems: number }>, options: { more?: boolean, force?: boolean } = {}) {
    if (import.meta.server || !customerId.value)
      return
    const target = state.value[key]
    if (target.loading || (target.loaded && !options.more && !options.force))
      return
    if (options.more && target.loaded && target.items.length >= target.totalItems)
      return
    const generation = state.value.generation
    let skip = options.more && target.loaded ? target.items.length : 0
    target.loading = true
    target.error = null
    try {
      for (;;) {
        const revision = state.value.revision
        const result = await read(skip)
        if (!current(generation))
          return
        if (revision !== state.value.revision) {
          skip = 0
          continue
        }
        // Every result comes from the server; never move estimates into the wallet.
        target.items = (skip ? [...target.items, ...result.items] : result.items) as RewardsState[K]['items']
        target.totalItems = result.totalItems
        target.loaded = true
        return
      }
    }
    catch (error) {
      if (current(generation)) {
        target.error = failureCode(error)
        if (failureCode(error) === 'NOT_LOGGED_IN') {
          state.value = initialState(scope.value, generation + 1)
          state.value.error = 'NOT_LOGGED_IN'
        }
      }
    }
    finally {
      target.loading = false
    }
  }
  const take = 20
  const loadHistory = (options?: { more?: boolean, force?: boolean }) => readPage('history', async (skip) => {
    const { data, errors } = await inContext(() => useGraphqlQuery('verocHistory', { skip, take }))
    throwOnErrors(errors)
    requireCurrentCustomer(data?.activeCustomer)
    return data.activeCustomerVerocCoinsHistory
  }, options)
  const loadLots = (options?: { more?: boolean, force?: boolean }) => readPage('lots', async (skip) => {
    const { data, errors } = await inContext(() => useGraphqlQuery('verocLots', { skip, take }))
    throwOnErrors(errors)
    requireCurrentCustomer(data?.activeCustomer)
    return data.activeCustomerVerocCoinsLots
  }, options)
  const loadAwaitingPayment = (options?: { more?: boolean, force?: boolean }) => readPage('awaitingPayment', async (skip) => {
    const { data, errors } = await inContext(() => useGraphqlQuery('verocAwaitingPaymentLots', { skip, take }))
    throwOnErrors(errors)
    requireCurrentCustomer(data?.activeCustomer)
    return data.activeCustomerVerocCoinsLots
  }, options)
  const loadNotifications = (options?: { more?: boolean, force?: boolean }) => readPage('notifications', async (skip) => {
    const { data, errors } = await inContext(() => useGraphqlQuery('verocNotifications', { skip, take }))
    throwOnErrors(errors)
    requireCurrentCustomer(data?.activeCustomer)
    return data.activeCustomerVerocNotifications
  }, options)
  const loadOrderRewards = (options?: { more?: boolean, force?: boolean }) => readPage('orderRewards', async (skip) => {
    const { data, errors } = await inContext(() => useGraphqlQuery('verocOrderRewards', { skip, take }))
    throwOnErrors(errors)
    requireCurrentCustomer(data?.activeCustomer)
    return data.activeCustomerVerocOrderRewards
  }, options)
  const loadReviewPurchases = (options?: { more?: boolean, force?: boolean }) => readPage('reviewPurchases', async (skip) => {
    const { data, errors } = await inContext(() => useGraphqlQuery('verocReviewPurchases', { skip, take }))
    throwOnErrors(errors)
    requireCurrentCustomer(data?.activeCustomer)
    return data.activeCustomerVerocReviewPurchases
  }, options)
  const loaders = { history: loadHistory, lots: loadLots, awaitingPayment: loadAwaitingPayment, notifications: loadNotifications, orderRewards: loadOrderRewards, reviewPurchases: loadReviewPurchases }

  async function refresh() {
    const generation = state.value.generation
    const drawer = await inContext(() => useRewardsDrawer())
    if (!current(generation))
      return
    const visiblePages: Partial<Record<typeof drawer.section.value, PageKey[]>> = {
      overview: ['orderRewards'],
      activity: ['history', 'lots', 'awaitingPayment'],
      notifications: ['notifications'],
      reviews: ['reviewPurchases'],
    }
    const activePages = drawer.visible.value ? visiblePages[drawer.section.value] ?? [] : []
    for (const key of Object.keys(loaders) as PageKey[]) {
      if (!activePages.includes(key))
        state.value[key].loaded = false
    }
    // Hidden sections are re-read when opened, starting at page one. Visiting
    // history once must not add all its queries to every checkout toggle.
    await Promise.all([load(true), ...activePages.filter(key => state.value[key].loaded).map(key => loaders[key]({ force: true }))])
  }

  /** One credit write at a time across all consumers. Never replay a queued click for another account. */
  async function write<T>(action: () => Promise<T>, apply?: (result: T) => void | Promise<void>): Promise<T | null> {
    if (import.meta.server || !customerId.value)
      throw new Error('NOT_LOGGED_IN')
    if (state.value.busy)
      throw new Error('BUSY')
    const generation = state.value.generation
    state.value.busy = true
    state.value.error = null
    try {
      const result = await action()
      if (!current(generation))
        return null
      state.value.revision++
      await apply?.(result)
      if (!current(generation))
        return null
      // Follow-up reads are independent of the committed mutation's success.
      await refresh()
      return current(generation) ? result : null
    }
    catch (error) {
      if (!current(generation))
        return null
      state.value.error = failureCode(error)
      if (failureCode(error) === 'ALREADY_CLAIMED') {
        state.value.revision++
        await refresh()
      }
      throw error
    }
    finally {
      if (current(generation))
        state.value.busy = false
    }
  }

  async function refreshClaimOrder(generation: number, orderId: string | undefined) {
    const activeOrder = nuxtApp.$activeOrder?.activeOrder
    if (!activeOrder || !orderId)
      return
    const isCurrent = () => current(generation) && String(activeOrder.value?.id) === orderId
    try {
      const { enqueue } = await inContext(() => useRequestQueue())
      const store = await inContext(() => useActiveOrderStore())
      await enqueue(async () => {
        if (!isCurrent())
          return
        const { data, errors } = await inContext(() => useGraphqlQuery('activeOrder'))
        if (!isCurrent() || errors?.length || String(data?.activeOrder?.id) !== orderId)
          return
        store.publishActiveOrder(data.activeOrder)
        activeOrder.value = data.activeOrder
      })
    }
    catch {
      // The credit already succeeded. An unavailable checkout refresh must not
      // turn it into a failed claim or invite the customer to claim twice.
    }
  }

  function claim(action: () => Promise<RewardsClaim>) {
    const generation = state.value.generation
    const orderId = nuxtApp.$activeOrder?.activeOrder.value?.id
    return write(async () => {
      const result = await action()
      if (!result.success)
        throw new Error(result.errorCode || 'FAILED')
      return result
    }, async (result) => {
      if (result.wallet)
        state.value.wallet = result.wallet
      // Celebration uses only coins confirmed by the successful server credit.
      state.value.claimResult = result.coins > 0 ? result : null
      if (result.coins > 0)
        await refreshClaimOrder(generation, orderId == null ? undefined : String(orderId))
    })
  }
  const claimMission = (key: string, referenceId?: string | null) => {
    const mission = state.value.account?.missions.find(item => item.key === key)
    if (!mission)
      return Promise.reject(new Error('NOT_AVAILABLE'))
    if (!isRewardMissionVisible(mission) || isMobileRewardMission(mission))
      return Promise.reject(new Error('APP_REQUIRED'))
    return claim(async () => {
      const { data, errors } = await inContext(() => useGraphqlMutation('claimVerocMission', { key, referenceId }))
      throwOnErrors(errors)
      return data.claimVerocCoinsMission
    })
  }
  const claimMilestone = (key: string) => claim(async () => {
    const { data, errors } = await inContext(() => useGraphqlMutation('claimVerocMilestone', { key }))
    throwOnErrors(errors)
    return data.claimVerocCoinsMilestone
  })
  const claimOrder = (orderId: string) => claim(async () => {
    const { data, errors } = await inContext(() => useGraphqlMutation('claimVerocOrderReward', { orderId }))
    throwOnErrors(errors)
    return data.claimVerocCoinsOrderReward
  })
  const attachReferral = (code: string) => claim(async () => {
    const { data, errors } = await inContext(() => useGraphqlMutation('attachVerocReferral', { code: code.trim() }))
    throwOnErrors(errors)
    return data.attachVerocCoinsReferral
  })
  const acceptTerms = () => write(async () => {
    const version = state.value.programme?.termsVersion
    if (!version)
      throw new Error('FAILED')
    const { data, errors } = await inContext(() => useGraphqlMutation('acceptVerocTerms', { version }))
    throwOnErrors(errors)
    if (!data.acceptVerocCoinsTerms)
      throw new Error('FAILED')
    return true
  })
  const setBirthDate = (date: string) => write(async () => {
    const { data, errors } = await inContext(() => useGraphqlMutation('setVerocBirthDate', { date }))
    throwOnErrors(errors)
    if (!data.setVerocCoinsBirthDate)
      throw new Error('FAILED')
    return true
  })
  const acknowledgeLevel = () => write(async () => {
    const { data, errors } = await inContext(() => useGraphqlMutation('acknowledgeVerocLevel'))
    throwOnErrors(errors)
    if (!data.acknowledgeVerocCoinsLevel)
      throw new Error('FAILED')
    return true
  })
  const markNotificationsRead = (ids?: string[]) => write(async () => {
    const { data, errors } = await inContext(() => useGraphqlMutation('markVerocNotificationsRead', { ids }))
    throwOnErrors(errors)
    if (!data.markVerocCoinsNotificationsRead)
      throw new Error('FAILED')
    return true
  })
  const createReview = (input: CreateProductReviewInput) => write(async () => {
    const { data, errors } = await inContext(() => useGraphqlMutation('createVerocProductReview', { input }))
    throwOnErrors(errors)
    if (!('success' in data.createProductReview) || !data.createProductReview.success)
      throw new Error('errorCode' in data.createProductReview ? data.createProductReview.errorCode : 'FAILED')
    return true
  })

  return {
    wallet: field('wallet'),
    programme: field('programme'),
    account: field('account'),
    benefits: field('benefits'),
    loading: field('loading'),
    loaded: field('loaded'),
    busy: field('busy'),
    error: field('error'),
    claimResult: field('claimResult'),
    revision: field('revision'),
    history: field('history'),
    lots: field('lots'),
    awaitingPayment: field('awaitingPayment'),
    notifications: field('notifications'),
    orderRewards: field('orderRewards'),
    reviewPurchases: field('reviewPurchases'),
    customerId,
    scope: computed(() => `${scope.value}:${state.value.generation}`),
    load,
    loadProgramme,
    refresh,
    loadHistory,
    loadLots,
    loadAwaitingPayment,
    loadNotifications,
    loadOrderRewards,
    loadReviewPurchases,
    claimMission,
    claimMilestone,
    claimOrder,
    attachReferral,
    acceptTerms,
    setBirthDate,
    acknowledgeLevel,
    markNotificationsRead,
    createReview,
    dismissClaim: () => { state.value.claimResult = null },
  }
}
